/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined, CopyOutlined } from '@ant-design/icons'
import { App, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import type { Livestream } from '@sportninja/common/types/Livestream'
import { useLivestreamMutations } from '@sportninja/common/hooks/useLivestreamMutations'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import dayjs from 'dayjs'
import LivestreamPlayer from './LivestreamPlayer'

interface LivestreamDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  livestream: Livestream | null
  typeLabel: string
  gameId: string
}

interface StreamDetails {
  stream_key: string
  rtmp_url: string
}

const statusConfig = {
  active: { color: colors.SUCCESS_300, label: 'Live', textColor: colors.WHITE },
  idle: { color: colors.WARNING_300, label: 'Idle', textColor: colors.BLACK },
  disabled: {
    color: colors.NEUTRAL_70,
    label: 'Disabled',
    textColor: colors.BLACK,
  },
}

const LivestreamDetailsModal: React.FC<LivestreamDetailsModalProps> = ({
  isOpen,
  onClose,
  livestream,
  typeLabel,
  gameId,
}) => {
  const { message } = App.useApp()
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 1024, [width])
  const { getStreamDetails } = useLivestreamMutations(gameId)
  const [streamDetails, setStreamDetails] = useState<StreamDetails | null>(null)

  useEffect(() => {
    if (isOpen && livestream) {
      getStreamDetails(livestream.type)
        .then((details) => {
          setStreamDetails({
            stream_key: details.stream_key,
            rtmp_url: details.rtmp_url,
          })
        })
        .catch(() => {
          setStreamDetails(null)
        })
    } else {
      setStreamDetails(null)
    }
  }, [isOpen, livestream, getStreamDetails])

  const handleCopy = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text)
        message.success(`${label} copied to clipboard`)
      } catch {
        message.error('Failed to copy')
      }
    },
    [message]
  )

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  if (!livestream) {
    return null
  }

  const statusStyle = statusConfig[livestream.status] || statusConfig.idle

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '900px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: auto;
          display: flex;
          flex-direction: column;
          padding: 16px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 900}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      maskClosable={true}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: ${isMobile ? 'column' : 'row'};
          gap: 16px;
        `}
      >
        {/* Video Player Section */}
        <div
          css={css`
            flex: ${isMobile ? '1' : '1.5'};
            min-width: 0;
          `}
        >
          <LivestreamPlayer streamUrl={livestream.stream_url} />
        </div>

        {/* Info Panel */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 12px;
            min-width: 280px;
            padding-right: 16px;
          `}
        >
          {/* Stream Type */}
          <InfoRow label='Stream Type' value={typeLabel} />

          {/* Status */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
            `}
          >
            <Text variant='paragraphSmall' color={colors.NEUTRAL_70}>
              Status
            </Text>
            <span
              css={css`
                padding: 4px 12px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 600;
                text-transform: uppercase;
                background: ${statusStyle.color};
                color: ${statusStyle.textColor};
                display: inline-block;
                width: fit-content;
              `}
            >
              {statusStyle.label}
            </span>
          </div>

          {/* Stream ID */}
          <InfoRowWithCopy
            label='Stream ID'
            value={livestream.id}
            onCopy={() => handleCopy(livestream.id, 'Stream ID')}
          />

          {/* Playback ID */}
          {livestream.playback_id && (
            <InfoRowWithCopy
              label='Playback ID'
              value={livestream.playback_id}
              onCopy={() => handleCopy(livestream.playback_id!, 'Playback ID')}
            />
          )}

          {/* Stream URL */}
          {livestream.stream_url && (
            <InfoRowWithCopy
              label='Stream URL'
              value={livestream.stream_url}
              onCopy={() => handleCopy(livestream.stream_url!, 'Stream URL')}
            />
          )}

          {/* RTMP URL - Only show if available */}
          {streamDetails?.rtmp_url && (
            <InfoRowWithCopy
              label='RTMP URL'
              value={streamDetails.rtmp_url}
              onCopy={() => handleCopy(streamDetails.rtmp_url, 'RTMP URL')}
            />
          )}

          {/* Stream Key - Only show if available */}
          {streamDetails?.stream_key && (
            <InfoRowWithCopy
              label='Stream Key'
              value={streamDetails.stream_key}
              onCopy={() => handleCopy(streamDetails.stream_key, 'Stream Key')}
            />
          )}

          {/* Created */}
          <InfoRow
            label='Created'
            value={dayjs(livestream.created_at).format('MMM DD, YYYY h:mm A')}
          />

          {/* Last Updated */}
          <InfoRow
            label='Last Updated'
            value={dayjs(livestream.updated_at).format('MMM DD, YYYY h:mm A')}
          />
        </div>
      </div>
    </Modal>
  )
}

interface InfoRowProps {
  label: string
  value: string
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      gap: 4px;
    `}
  >
    <Text variant='paragraphSmall' color={colors.NEUTRAL_70}>
      {label}
    </Text>
    <Text variant='paragraphMedium' color={colors.WHITE}>
      {value}
    </Text>
  </div>
)

interface InfoRowWithCopyProps {
  label: string
  value: string
  onCopy: () => void
}

const InfoRowWithCopy: React.FC<InfoRowWithCopyProps> = ({
  label,
  value,
  onCopy,
}) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      gap: 4px;
    `}
  >
    <Text variant='paragraphSmall' color={colors.NEUTRAL_70}>
      {label}
    </Text>
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      `}
    >
      <span
        css={css`
          color: ${colors.WHITE};
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
          min-width: 0;
        `}
        title={value}
      >
        {value}
      </span>
      <CopyOutlined
        onClick={onCopy}
        css={css`
          cursor: pointer;
          color: ${colors.PRIMARY};
          flex-shrink: 0;
          &:hover {
            opacity: 0.7;
          }
        `}
      />
    </div>
  </div>
)

export default LivestreamDetailsModal
